import { Link } from "@tanstack/react-router";
import type { GoalkeeperStats } from "@/api/ranking";
import type { ColumnDef } from "@tanstack/react-table";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { getPointsBadge } from "@/lib/utils";

export const goalkeeperStatsTableColumns: ColumnDef<GoalkeeperStats>[] = [
    {
        id: "rank",
        header: "Rank",
        size: 72,
        cell: ({ row, table }) => {
            const allData = table.getCoreRowModel().rows;
            const sortedData = [...allData].sort(
                (a, b) => b.original.totalPoints - a.original.totalPoints
            );

            const rank = sortedData.findIndex((r) => r.id === row.id) + 1;
            const label = `#${rank.toString().padStart(2, "0")}`;

            return (
                <span
                    className={`font-headline font-bold text-sm ${
                        rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                    }`}
                >
                    {label}
                </span>
            );
        },
    },
    {
        id: "player",
        header: "Player name",
        size: 200,
        cell: ({ row }) => (
            <Link
                to="/player/$playerId"
                params={{ playerId: row.original.playerId.toString() }}
                className="font-bold text-on-surface hover:text-primary hover:underline transition-colors"
            >
                {row.original.firstName} {row.original.lastName}
            </Link>
        ),
    },
    {
        accessorKey: "role",
        header: "Role",
        cell: ({ getValue }) => {
            const value = getValue() as string;
            return (
                <span className="px-2 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant">
                    {value}
                </span>
            );
        },
    },
    {
        accessorKey: "pointsGSAx",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">GSAx</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Goals Saved Above Expected - Goalkeeper performance relative to expected goals.</p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsHighDangerShots",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">High Danger Shots</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Performance on high danger shots.</p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsLowDangerShots",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Low Danger Shots</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Performance on low danger shots.</p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsLateralPassShots",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Shots From Lateral Pass</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Performance on shots following lateral passes.</p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsScreenedShots",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Screened Shots</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Performance on screened shots.</p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "totalPoints",
        header: "Total Points",
        cell: ({ getValue }) => {
            const value = getValue() as number;
            if (value === 0) {
                return (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="cursor-help text-on-surface-variant">-</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Player has spent insufficient time on the ice to be objectively evaluated against our KPI scale.</p>
                        </TooltipContent>
                    </Tooltip>
                );
            }
            const { bg, text, border } = getPointsBadge(value);
            return (
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${bg} ${text} rounded font-bold text-sm border ${border}`}>
                    {value}
                </div>
            );
        },
    },
];
